import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUserData } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        toast.error('Authentication failed - no session ID');
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          throw new Error('Failed to authenticate');
        }

        const userData = await response.json();
        setUserData(userData);
        toast.success(`Welcome, ${userData.name}!`);
        
        // Check if user needs onboarding
        const goalResponse = await fetch(`${API_URL}/api/goals/active`, {
          credentials: 'include',
        });
        
        if (goalResponse.ok) {
          const goal = await goalResponse.json();
          if (goal) {
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/onboarding', { replace: true });
          }
        } else {
          navigate('/onboarding', { replace: true });
        }
      } catch (error) {
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, setUserData]);

  return (
    <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center mx-auto mb-6 animate-pulse">
          <Leaf className="w-8 h-8 text-[#8BAA9E]" strokeWidth={1.5} />
        </div>
        <p className="text-[#B5B1AB] font-sans">Completing authentication...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
